import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TextField from "@mui/material/TextField";
import TableHead from "@mui/material/TableHead";
import Autocomplete from "@mui/material/Autocomplete";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import {
  CircularProgress,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Divider,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import { Download, Close, Person, RestartAlt } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { GetUsers, UpdateUserDeletedStatus } from ".";
import { Country, State, City } from "country-state-city";
import "./index.scss";
import { StyledTableCell, StyledTableRow } from "../../utils/constantData";
import { AdminPageLayout, AdminSectionCard, TableRowActions, AdminAction } from "../../components/admin";

const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const downloadUsersCsv = (users) => {
  const headers = ["Name", "Mobile", "State"];
  const rows = users.map((user) =>
    [escapeCsv(user.name), escapeCsv(user.mobile), escapeCsv(user.state)].join(",")
  );
  const blob = new Blob([[headers.join(","), ...rows].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "users.csv";
  link.click();
  URL.revokeObjectURL(url);
};

function UserDetailField({ label, value }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25, color: "#172112", wordBreak: "break-all" }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

function UserDetailPopup({ user, open, onClose, onStatusChange }) {
  const [isDeleted, setIsDeleted] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState({ open: false, message: "", severity: "success" });

  React.useEffect(() => {
    if (user) setIsDeleted(!!user.isDeleted);
  }, [user]);

  const handleToggle = async (e) => {
    const newValue = e.target.checked;
    setSaving(true);
    try {
      await UpdateUserDeletedStatus(user.id, newValue);
      setIsDeleted(newValue);
      onStatusChange(user.id, newValue);
      setToast({
        open: true,
        message: newValue ? "User soft-deleted successfully" : "User restored successfully",
        severity: "success",
      });
    } catch (err) {
      console.error(err);
      setToast({ open: true, message: "Failed to update status", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            boxShadow: "0 24px 64px rgba(0,0,0,0.12)",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #182414, #2a3d1e)",
            color: "#ecf3e7",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 2,
            px: 3,
          }}
        >
          <Person sx={{ color: "#9bd53d" }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#fff" }}>
              {user.name || "Unknown User"}
            </Typography>
            <Typography variant="caption" sx={{ color: "#8ab87b" }}>
              ID: {user.id}
            </Typography>
          </Box>
          <Chip
            label={isDeleted ? "Soft Deleted" : "Active"}
            size="small"
            sx={{
              background: isDeleted ? "rgba(197,84,61,0.2)" : "rgba(155,213,61,0.2)",
              color: isDeleted ? "#f4917e" : "#9bd53d",
              fontWeight: 700,
              border: `1px solid ${isDeleted ? "#f4917e" : "#9bd53d"}`,
            }}
          />
          <IconButton onClick={onClose} sx={{ color: "#ecf3e7", ml: 1 }} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
          {/* Two-column grid */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
            <UserDetailField label="Mobile" value={user.mobile} />
            <UserDetailField label="Email" value={user.email} />
            <UserDetailField label="Gender" value={user.gender} />
            <UserDetailField label="Age" value={user.age} />
            <UserDetailField label="Profession" value={user.profession} />
            <UserDetailField label="Role" value={user.role} />
            <UserDetailField label="Language" value={user.languagePreference} />
            <UserDetailField label="Date of Birth" value={user.dob ? new Date(user.dob).toLocaleDateString() : null} />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Location */}
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
            Location
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px", mt: 1 }}>
            <UserDetailField label="State" value={user.state} />
            <UserDetailField label="District" value={user.district} />
            <UserDetailField label="Sub-District" value={user.subDistrict} />
            <UserDetailField label="Village" value={user.village} />
            <UserDetailField label="City" value={user.city} />
            <UserDetailField label="Pincode" value={user.pincode} />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* isDeleted toggle */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: isDeleted ? "rgba(197,84,61,0.06)" : "rgba(63,122,42,0.06)",
              border: `1px solid ${isDeleted ? "rgba(197,84,61,0.25)" : "rgba(63,122,42,0.25)"}`,
              borderRadius: "10px",
              px: 2,
              py: 1.5,
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={700} color={isDeleted ? "#c5543d" : "#3f7a2a"}>
                {isDeleted ? "Account is Soft Deleted" : "Account is Active"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isDeleted
                  ? "Toggle off to active this user's account"
                  : "Toggle on to de-active this user's account"}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isDeleted}
                  onChange={handleToggle}
                  disabled={saving}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#c5543d" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#c5543d" },
                  }}
                />
              }
              label={saving ? <CircularProgress size={14} /> : (isDeleted ? "Deleted" : "Active")}
              labelPlacement="start"
              sx={{ m: 0, gap: 1 }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <AdminAction onClick={onClose} variant="outline">
            Close
          </AdminAction>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} sx={{ borderRadius: "10px" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default function UserListPage() {
  const navigate = useNavigate();
  const [country, setCountry] = React.useState([]);
  const [states, SetStates] = React.useState([]);
  const [city, SetCity] = React.useState([]);
  const [code, setCode] = React.useState("IN");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [data, setData] = React.useState([]);
  const [valueCout, setValueCout] = React.useState("india");
  const [valueState, setValueState] = React.useState();
  const [valueCity, setValueCity] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [searchInput, setSearchInput] = React.useState("");
  const [activeTab, setActiveTab] = React.useState(0); // 0 = Active, 1 = Deleted
  const [filterKey, setFilterKey] = React.useState(0); // key to remount autocompletes on reset

  const hasActiveFilters = !!(searchInput || valueState || valueCity);

  // Popup state
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [popupOpen, setPopupOpen] = React.useState(false);

  const startIndex = page * rowsPerPage;

  // Derived list filtered by the selected tab
  const filteredData = data.filter((u) =>
    activeTab === 0 ? !u.isDeleted : !!u.isDeleted
  );

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const getuser = async () => {
    setLoading(true);
    let query = searchInput.trim().toLowerCase();
    let reqData = { city: valueCity, state: valueState, country: valueCout };
    try {
      const response = await GetUsers(reqData);
      if (response.status === 200) {
        setLoading(false);
        const users = response.data.data.data || response.data.data.users || [];
        let filtered = users.filter((user) => {
          const searchable = [
            user.name,
            user.mobile,
            user.email,
            user.state,
            user.district,
            user.subDistrict,
            user.village,
            user.city,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return searchable.includes(query);
        });
        setData(filtered);
      } else {
        throw new Error("Something went wrong, please try again later.");
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const count = Country.getAllCountries();
    setCountry(count.map((i) => ({ id: i.isoCode, name: i.name })));
    const stat = State.getStatesOfCountry("IN");
    SetStates(stat.map((i) => ({ id: i.isoCode, name: i.name })));
  }, []);

  React.useEffect(() => {
    getuser();
  }, [valueCity, valueState, searchInput]);

  const handleState = (value) => {
    if (value) {
      setValueState(value.name);
      const c = City.getCitiesOfState(code, value.id);
      SetCity(c.map((i) => ({ id: i.isoCode, name: i.name })));
    } else {
      setValueState("");
      SetCity("");
    }
  };

  const handleCity = (value) => {
    if (value) setValueCity(value.name);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setPopupOpen(true);
  };

  // Update isDeleted in local table without full refetch
  const handleStatusChange = (userId, isDeleted) => {
    setData((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, isDeleted } : u))
    );
    if (selectedUser?.id === userId) {
      setSelectedUser((u) => ({ ...u, isDeleted }));
    }
  };

  return (
    <>
      <AdminPageLayout
        eyebrow="User Management"
        title="App Users"
        description="Browse registered users, search by name, and filter by state or city."
        actions={
          <div className="admin-toolbar" style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <TextField
              label="Search users"
              value={searchInput}
              size="small"
              sx={{ minWidth: 220 }}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <Autocomplete
              key={`state-${filterKey}`}
              disablePortal
              id="state"
              options={states}
              getOptionLabel={(option) => option.name}
              size="small"
              sx={{ minWidth: 230 }}
              onChange={(event, newValue) => handleState(newValue)}
              renderInput={(params) => <TextField {...params} label="State" />}
            />
            <Autocomplete
              key={`city-${filterKey}`}
              disablePortal
              id="city"
              options={city}
              getOptionLabel={(option) => option.name}
              size="small"
              sx={{ minWidth: 160 }}
              onChange={(event, newValue) => handleCity(newValue)}
              disabled={city.length === 0}
              renderInput={(params) => <TextField {...params} label="City" />}
            />
            <AdminAction
              id="reset-filters-btn"
              variant="outline"
              startIcon={<RestartAlt />}
              disabled={!hasActiveFilters}
              onClick={() => {
                setSearchInput("");
                setValueState(undefined);
                setValueCity("");
                SetCity([]);
                setPage(0);
                setFilterKey((k) => k + 1);
              }}
              sx={{
                whiteSpace: "nowrap",
                borderColor: hasActiveFilters ? "rgba(63,122,42,0.4)" : undefined,
                color: hasActiveFilters ? "#3f7a2a" : undefined,
                "&:hover": hasActiveFilters ? { borderColor: "#3f7a2a", background: "rgba(63,122,42,0.06)" } : {},
              }}
            >
              Reset
            </AdminAction>
            <AdminAction
              variant="outline"
              startIcon={<Download />}
              onClick={() => downloadUsersCsv(data)}
              disabled={!data.length}
            >
              CSV
            </AdminAction>
            {/* <Button
              variant="contained"
              onClick={() => navigate("/users/add")}
              sx={{
                background: "#292524",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                whiteSpace: "nowrap",
                px: 2.5,
                "&:hover": { background: "#44403c" },
              }}
            >
              + Add New User
            </Button> */}
          </div>
        }
      >
        <AdminSectionCard
          title=""
          description=""
          className="admin-table-shell"
        >
          {/* Tab filter — same design as Video Dashboard */}
          <Box sx={{ px: 2, pt: 1, borderBottom: "1px solid rgba(39,55,24,0.08)" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "#3f7a2a",
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  color: "#62715d",
                  minHeight: 44,
                  "&.Mui-selected": {
                    color: "#3f7a2a",
                    fontWeight: 700,
                  },
                },
              }}
            >
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Active
                    <Chip
                      label={data.filter((u) => !u.isDeleted).length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        background: activeTab === 0 ? "rgba(63,122,42,0.12)" : "rgba(39,55,24,0.06)",
                        color: activeTab === 0 ? "#3f7a2a" : "#62715d",
                      }}
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Deleted
                    <Chip
                      label={data.filter((u) => !!u.isDeleted).length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        background: activeTab === 1 ? "rgba(197,84,61,0.12)" : "rgba(39,55,24,0.06)",
                        color: activeTab === 1 ? "#c5543d" : "#62715d",
                      }}
                    />
                  </Box>
                }
              />
            </Tabs>
          </Box>
          <TableContainer component="div">
            <Table sx={{ minWidth: 700 }} aria-label="users table">
              <TableHead>
                <StyledTableCell>Sr. No.</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Location</StyledTableCell>
                <StyledTableCell>Profile</StyledTableCell>
                <StyledTableCell align="right">Contact</StyledTableCell>
                <StyledTableCell align="center">Status</StyledTableCell>
                <StyledTableCell align="right">Actions</StyledTableCell>
              </TableHead>
              <TableBody>
                {filteredData.length !== 0 ? (
                  filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <StyledTableRow key={row.id}>
                        <StyledTableCell component="th">
                          {startIndex + index + 1}
                        </StyledTableCell>
                        <StyledTableCell>{row.name}</StyledTableCell>
                        <StyledTableCell>
                          {[row.village, row.subDistrict, row.district, row.state]
                            .filter(Boolean)
                            .join(", ") ||
                            row.city ||
                            "N/A"}
                        </StyledTableCell>
                        <StyledTableCell>
                          {[row.gender, row.role].filter(Boolean).join(" / ") || "N/A"}
                        </StyledTableCell>
                        <StyledTableCell align="right">{row.mobile}</StyledTableCell>
                        <StyledTableCell align="center">
                          <Chip
                            label={row.isDeleted ? "Deleted" : "Active"}
                            size="small"
                            sx={{
                              background: row.isDeleted
                                ? "rgba(197,84,61,0.1)"
                                : "rgba(63,122,42,0.1)",
                              color: row.isDeleted ? "#c5543d" : "#3f7a2a",
                              fontWeight: 700,
                              fontSize: "0.7rem",
                              border: `1px solid ${row.isDeleted ? "rgba(197,84,61,0.3)" : "rgba(63,122,42,0.3)"}`,
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <TableRowActions
                            rowId={row.id}
                            onView={() => handleViewUser(row)}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                ) : loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8">
                      <CircularProgress size={20} className="text-stone-500" />
                      <span className="ml-2 text-stone-500">Loading...</span>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-stone-500">
                      No {activeTab === 0 ? "active" : "deleted"} users matched the current filters.
                    </td>
                  </tr>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-toolbar": { borderTop: "1px solid #e7e5e4" },
            }}
          />
        </AdminSectionCard>
      </AdminPageLayout>

      <UserDetailPopup
        user={selectedUser}
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        onStatusChange={handleStatusChange}
      />
    </>
  );
}
